import { createServerFn } from "@tanstack/react-start";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { dbError } from "./db";

const shippingAddressSchema = z.object({
  cep: z.string(),
  logradouro: z.string(),
  numero: z.string(),
  complemento: z.string().optional().default(""),
  bairro: z.string(),
  cidade: z.string(),
  uf: z.string(),
});

const orderItemSchema = z.object({
  productId: z.string(),
  slug: z.string(),
  name: z.string(),
  price: z.number(),
  image: z.string(),
  quantity: z.number().int().positive(),
});

export const createOrderSchema = z.object({
  customerName: z.string().min(3),
  customerEmail: z.string().email(),
  customerPhone: z.string(),
  customerCpf: z.string(),
  shippingAddress: shippingAddressSchema,
  items: z.array(orderItemSchema).min(1),
  subtotal: z.number().nonnegative(),
  shippingCost: z.number().nonnegative().default(0),
  shippingServiceId: z.string(),
  shippingServiceName: z.string(),
  total: z.number().nonnegative(),
  couponCode: z.string().trim().min(1).optional(),
});

export type CreateOrderInput = z.infer<typeof createOrderSchema>;

export interface OrderRecord {
  id: string;
  order_number: number;
  customer_name: string;
  status: string;
  payment_method: string | null;
  total: number;
  items: CreateOrderInput["items"];
  shipping_address: CreateOrderInput["shippingAddress"];
  shipping_service_id: string | null;
  shipping_service_name: string | null;
  created_at: string;
}

export async function insertPendingOrder(
  data: CreateOrderInput,
  userId: string | null = null,
  couponCode: string | null = null,
  discountAmount: number = 0,
): Promise<{ id: string; orderNumber: number }> {
  const { data: row, error } = await supabaseAdmin
    .from("orders")
    .insert({
      customer_name: data.customerName,
      customer_email: data.customerEmail,
      customer_phone: data.customerPhone,
      customer_cpf: data.customerCpf,
      shipping_address: data.shippingAddress,
      items: data.items,
      subtotal: data.subtotal,
      shipping_cost: data.shippingCost,
      shipping_service_id: data.shippingServiceId,
      shipping_service_name: data.shippingServiceName,
      total: data.total,
      status: "pending",
      user_id: userId,
      coupon_code: couponCode,
      discount_amount: discountAmount,
    })
    .select("id, order_number")
    .single();

  if (error) throw dbError(error, "src/lib/orders.functions.ts");
  return { id: row.id, orderNumber: row.order_number };
}

export const getOrder = createServerFn({ method: "GET" })
  .validator((id: string) => z.string().uuid().parse(id))
  .handler(async ({ data: id }): Promise<OrderRecord | null> => {
    const { data, error } = await supabaseAdmin
      .from("orders")
      .select(
        "id, order_number, customer_name, status, payment_method, total, items, shipping_address, shipping_service_id, shipping_service_name, created_at",
      )
      .eq("id", id)
      .maybeSingle();

    if (error) throw dbError(error, "src/lib/orders.functions.ts");
    return data as unknown as OrderRecord | null;
  });

export const orderQuery = (id: string) =>
  queryOptions({
    queryKey: ["order", id],
    queryFn: () => getOrder({ data: id }),
  });

export const getMyOrders = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<OrderRecord[]> => {
    // Usa o client autenticado (respeita RLS) em vez de supabaseAdmin, para
    // que o filtro por dono do pedido seja garantido pelo banco, não pela
    // aplicação.
    const { data, error } = await context.supabase
      .from("orders")
      .select(
        "id, order_number, customer_name, status, payment_method, total, items, shipping_address, shipping_service_id, shipping_service_name, created_at",
      )
      .order("created_at", { ascending: false })
      .limit(100);

    if (error) throw dbError(error, "src/lib/orders.functions.ts");
    return (data ?? []) as unknown as OrderRecord[];
  });

export const myOrdersQuery = () =>
  queryOptions({
    queryKey: ["my-orders"],
    queryFn: () => getMyOrders(),
  });
